'use client';

import React, { useMemo } from 'react';
import LinkNoPrefetch from '@/components/link-noprefetch';
import Image from 'next/image';
import { LinkSection } from './components/LinkSection';

const socialIcons = [
    {
        href: 'https://x.com/dotcms',
        src: '/assets/footer/social-icon-x.svg',
        alt: 'dotCMS X/Twitter account'
    },
    {
        href: 'https://www.linkedin.com/company/dotcms/',
        src: '/assets/footer/social-icon-linkedin.svg',
        alt: 'dotCMS LinkedIn'
    },
    {
        href: 'https://www.instagram.com/dotCMS/',
        src: '/assets/footer/social-icon-instagram.svg',
        alt: 'dotCMS Instagram'
    },
    {
        href: 'https://www.facebook.com/dotCMS/',
        src: '/assets/footer/social-icon-facebook.svg',
        alt: 'dotCMS Facebook'
    },
    {
        href: 'https://www.youtube.com/user/javacms/videos',
        src: '/assets/footer/social-icon-youtube.svg',
        alt: 'dotCMS YouTube Channel'
    }
];

const certificationIcons = [
    {
        src: '/assets/footer/aicpasoc.svg',
        alt: 'aicpasoc certification icon'
    },
    {
        src: '/assets/footer/iso-27001.png',
        alt: 'ISO/IEC 27001:2022 certified'
    },
    {
        src: '/assets/footer/iso-42001.png',
        alt: 'ISO/IEC 42001:2023 certified'
    },
    {
        src: '/assets/footer/txramp.webp',
        alt: 'txramp certification icon'
    }
];

const footerLinks = [
    {
        href: 'https://dev.dotcms.com/docs/privacy-policy',
        text: 'Privacy'
    },
    {
        href: '/company/policies/accessibility-statement',
        text: 'Accessibility Statement'
    },
    {
        href: 'https://dev.dotcms.com/docs/manage/access-and-security/security-and-privacy/gdpr-and-ccpa-compliance-support',
        text: 'GDPR Support'
    },
    {
        href: '/',
        text: 'Cookie Settings',
        onClick: () => {
            var _hsp = (window._hsp = window._hsp || []);
            _hsp.push(['showBanner']);
        }
    }
];

function Footer() {
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    return (
        <footer className="bg-blue text-white">
            <div className="container mx-auto flex justify-between pt-16 md:py-16">
                <div className="hidden max-w-60 flex-col justify-between lg:flex">
                    <div>
                        <div className="flex flex-wrap gap-2">
                            {certificationIcons.map((cert, index) => (
                                <div key={index} className="relative size-12">
                                    <Image
                                        src={cert.src}
                                        width={0}
                                        height={0}
                                        sizes="50px"
                                        alt={cert.alt}
                                        className="h-auto w-full"
                                    />
                                </div>
                            ))}
                        </div>
                        <p className="mr-4 mt-6 text-xs text-custom-transparent">
                            Simplify content creation and delivery to save costs and grow revenue.
                        </p>
                    </div>
                    <LinkNoPrefetch href="/" className="relative h-6 w-34">
                        <Image
                            src="/assets/footer/dotcms-footer.svg"
                            width={0}
                            height={0}
                            sizes="136px"
                            alt="dotcms logo"
                            unoptimized
                        />
                    </LinkNoPrefetch>
                </div>
                <LinkSection />
            </div>
            <div className="container mx-auto px-4 pb-2 pt-10 md:pb-5 lg:hidden">
                <ul
                    className="flex flex-wrap items-center justify-center gap-3"
                    aria-label="Compliance certifications">
                    {certificationIcons.map((cert, index) => (
                        <li key={index} className="relative size-14">
                            <Image
                                src={cert.src}
                                width={0}
                                height={0}
                                sizes="56px"
                                alt={cert.alt}
                                className="h-auto w-full"
                            />
                        </li>
                    ))}
                </ul>
            </div>
            <div className="hidden w-full border-t border-t-custom-soft md:block" />
            <div className="container relative mx-auto flex flex-col pb-12 pt-10 md:flex-row md:items-center md:justify-between md:py-12">
                <div className="flex flex-wrap gap-2 text-xs text-custom-frost">
                    {footerLinks.map((link, index) => (
                        <React.Fragment key={index}>
                            <LinkNoPrefetch
                                href={link.href}
                                onClick={link.onClick}
                                className="text-slate-300 hover:text-white">
                                {link.text}
                            </LinkNoPrefetch>
                            {index < footerLinks.length - 1 && ' |'}
                        </React.Fragment>
                    ))}
                </div>
                <p className="text-slate-300">
                    Copyright © 2011-{currentYear} dotCMS, LLC All rights reserved.
                </p>
                <ul className="mt-4 flex gap-4 md:mt-0">
                    {socialIcons.map((icon, index) => (
                        <li key={index} className="relative size-8">
                            <LinkNoPrefetch href={icon.href} target="_blank">
                                <Image
                                    src={icon.src}
                                    width={0}
                                    height={0}
                                    sizes="32px"
                                    alt={icon.alt}
                                    className="hover:invert-1 h-auto w-full hover:brightness-[128%] hover:contrast-200 hover:hue-rotate-[46deg] hover:saturate-[182%]"
                                />
                            </LinkNoPrefetch>
                        </li>
                    ))}
                </ul>
            </div>
        </footer>
    );
}

export default Footer;
